// src/pages/tablet/TabletDashboard.tsx
// דשבורד טאבלט - מסך נוכחות (טרם הגיעו / נמצאים בגן)
// v2.1.1 - Added SMS checkbox feature - build 20260202
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const BUILD_VERSION = '2.1.1-20260202';

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';
import {
  useAttendanceStore,
  selectExpectedChildren,
  selectArrivedChildren,
  AttendanceChild,
} from '../../stores/attendanceStore';
import { useMessageStore, MessageTemplate, TemplateType, SentMessage } from '../../stores/messageStore';
import { useSimulationStore } from '../../stores/simulationStore';
import { proactiveTokenRefresh, syncApi } from '../../services/api';
import { useHelpGuide } from '../../hooks/useHelpGuide';
import HelpGuideModal from '../../components/common/HelpGuideModal';

// ===============================================
// Constants
// ===============================================

const COLORS = ['#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7', '#DDA0DD', '#98D8C8', '#F7DC6F'];
const getColor = (name: string) => COLORS[name.charCodeAt(0) % COLORS.length];

const AUTO_SWITCH_DELAY = 15000; // 15 שניות

// ===============================================
// Helper Functions
// ===============================================

// פונקציה לעיצוב תאריך לפי הלוקליזציה של הדפדפן בפורמט dd/MM/yyyy
const formatDate = (date: Date): string => {
  const locale = navigator.language || 'he-IL';
  try {
    return new Intl.DateTimeFormat(locale, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).format(date);
  } catch {
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  }
};

// המרת שעה לדקות עבור מיון והשוואה
const timeToMinutes = (time?: string): number => {
  if (!time) return 0;
  const [hours, minutes] = time.split(':').map(Number);
  return hours * 60 + (minutes || 0);
};

// בדיקה אם עברה שעת פתיחת הגן
// משתמש בשעון סימולציה אם פעיל, אחרת שעון אמיתי
const isPastOpeningTime = (openingTime: string): boolean => {
  const now = useSimulationStore.getState().getDisplayTime();
  const currentMinutes = now.getHours() * 60 + now.getMinutes();
  return currentMinutes >= timeToMinutes(openingTime);
};

// בדיקה אם השעה היא שעת סנכרון הבוקר (לפי הגדרות הגן או ברירת מחדל 6:00-7:00)
// משתמש בשעון סימולציה אם פעיל
const isMorningSyncTime = (morningSyncTime?: string): boolean => {
  const now = useSimulationStore.getState().getDisplayTime();
  const currentMinutes = now.getHours() * 60 + now.getMinutes();

  if (morningSyncTime) {
    // אם יש שעת סנכרון מוגדרת - בדיקה אם עברנו אותה
    const syncMinutes = timeToMinutes(morningSyncTime);
    return currentMinutes >= syncMinutes && currentMinutes <= syncMinutes + 60; // חלון של שעה
  }

  // ברירת מחדל: 6:00-7:00
  return currentMinutes >= 360 && currentMinutes <= 420;
};

// ===============================================
// Constants - Smart Sync
// ===============================================

// סנכרון חכם: 15 דקות אם לא כולם הגיעו, 30 דקות אם כולם הגיעו
const SYNC_INTERVAL_NOT_ALL_ARRIVED = 5 * 60 * 1000;  // 5 דקות (was 15)
const SYNC_INTERVAL_ALL_ARRIVED = 10 * 60 * 1000;     // 10 דקות (was 30)
const SYNC_AFTER_INACTIVITY = 5 * 60 * 1000;          // 5 דקות ללא פעילות (was 15)
const STOP_SYNC_AFTER_LATEST_ARRIVAL_MINUTES = 30;     // Stop periodic sync 30min after latestArrivalTime

type ViewMode = 'pending' | 'present';
type MessageMenuMode = 'closed' | 'menu' | 'select-child' | 'compose-child' | 'compose-all' | 'templates' | 'confirm' | 'view-messages' | 'view-child-messages';

// ===============================================
// Main Component
// ===============================================

export default function TabletDashboard() {
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const helpGuide = useHelpGuide();

  // Attendance Store
  const {
    children,
    kindergartenSettings,
    isLoading,
    isSyncing,
    syncError,
    hasUpdates,
    closedDayInfo,
    performMorningSync,
    performDeltaSync,
    markArrival,
    cancelArrival,
    getChildPhoto,
  } = useAttendanceStore();

  // Set tab title
  useEffect(() => {
    document.title = 'קיפיקיד — גן ילדים';
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', '#16a34a');
  }, []);


  // Local State
  const [time, setTime] = useState(new Date());
  const [toast, setToast] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('pending');
  const [searchQuery, setSearchQuery] = useState('');
  const [loadingChildId, setLoadingChildId] = useState<string | null>(null);
  const [lastActivityTime, setLastActivityTime] = useState<number>(Date.now());
  const [autoSwitchEnabled, setAutoSwitchEnabled] = useState(false);
  const [selectedChild, setSelectedChild] = useState<AttendanceChild | null>(null);

  // Message States
  const [messageMenuMode, setMessageMenuMode] = useState<MessageMenuMode>('closed');
  const [messageChild, setMessageChild] = useState<AttendanceChild | null>(null);
  const [messageSearchQuery, setMessageSearchQuery] = useState('');
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [customMessageText, setCustomMessageText] = useState('');
  const [finalMessageText, setFinalMessageText] = useState('');
  const [msgSearchChild, setMsgSearchChild] = useState('');
  const [msgSearchText, setMsgSearchText] = useState('');
  const [msgDateFrom, setMsgDateFrom] = useState('');
  const [msgDateTo, setMsgDateTo] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [sendToMobile, setSendToMobile] = useState(false); // שליחת SMS לנייד

  // Message Store
  const {
    templates,
    addTemplate,
    updateTemplate,
    deleteTemplate,
    sendMessage,
    getTemplatesByType,
    getMessagesByChild,
    getGeneralMessages,
    sentMessages,
    loadMessagesFromServer,
    isLoadingMessages,
  } = useMessageStore();

  // Template editing states
  const [editingTemplate, setEditingTemplate] = useState<MessageTemplate | null>(null);
  const [newTemplateName, setNewTemplateName] = useState('');
  const [newTemplateContent, setNewTemplateContent] = useState('');
  const [newTemplateType, setNewTemplateType] = useState<TemplateType>('child');

  // View messages state
  const [viewMessagesChild, setViewMessagesChild] = useState<AttendanceChild | null>(null);

  // Network state
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  // Refs
  const syncIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const autoSwitchIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Derived data
  const expectedChildren = useMemo(() => selectExpectedChildren({ children } as any), [children]);
  const arrivedChildren = useMemo(() => selectArrivedChildren({ children } as any), [children]);

  // ===============================================
  // Effects
  // ===============================================

  // בדיקת התחברות - כולל בדיקה שיש kindergartenPublicId
  useEffect(() => {
    if (!user || user.role !== 'tablet') {
      navigate('/tablet/login');
    } else if (!user.kindergartenPublicId) {
      // משתמש קיים אבל חסר מזהה גן - נדרשת כניסה מחדש
      console.warn('[TabletDashboard] User exists but kindergartenPublicId is missing, forcing re-login');
      logout();
      navigate('/tablet/login');
    }
  }, [user, navigate, logout]);

  // עדכון שעון — משתמש בשעון סימולציה אם פעיל
  const simStore = useSimulationStore();
  useEffect(() => {
    const t = setInterval(() => {
      setTime(simStore.getDisplayTime());
    }, 1000);
    return () => clearInterval(t);
  }, [simStore.active]);

  // סנכרון ראשוני וקביעת מצב תצוגה ברירת מחדל
  useEffect(() => {
    console.log('[TabletDashboard] Init effect triggered. kindergartenPublicId:', user?.kindergartenPublicId);
    if (!user?.kindergartenPublicId) return;

    const init = async () => {
      // סנכרון בוקר אם טרם בוצע היום
      const state = useAttendanceStore.getState();
      const lastSync = state.lastFullSync;
      const currentChildren = state.children;
      const settings = state.kindergartenSettings;
      const today = useSimulationStore.getState().getDisplayTime().toDateString();
      const lastSyncDate = lastSync ? new Date(lastSync).toDateString() : null;

      console.log('[TabletDashboard] Init check:', {
        lastSync,
        lastSyncDate,
        today,
        childrenCount: currentChildren.length,
        isSyncing: state.isSyncing,
        morningSyncTime: settings?.morningSyncTime,
      });

      // שעת סנכרון בוקר מהגדרות הגן
      const morningSyncTime = settings?.morningSyncTime;

      // סנכרון מלא אם:
      // 1. לא בוצע סנכרון היום, או
      // 2. אנחנו בטווח זמן סנכרון בוקר (לפי הגדרות הגן), או
      // 3. אין ילדים ב-state (אולי נמחקו מ-localStorage)
      const needsFullSync = lastSyncDate !== today ||
                           isMorningSyncTime(morningSyncTime) ||
                           currentChildren.length === 0;

      if (needsFullSync) {
        console.log('[TabletDashboard] Performing full sync. Reason:',
          lastSyncDate !== today ? 'new day' :
          isMorningSyncTime(morningSyncTime) ? 'morning sync time' :
          'no children in state');
        await performMorningSync(user.kindergartenPublicId!);
      }

      // קביעת מצב תצוגה ברירת מחדל לפי שעה — רק אם לא בסימולציה
      // בסימולציה: getDisplayTime() מחזיר שעת דפדפן כשהסטור עדיין לא נטען → אפקט ב-272-299 יטפל
      const simActive = useSimulationStore.getState().active;
      if (!simActive) {
        const displaySwitchTime = settings?.displaySwitchTime || '08:45';
        if (isPastOpeningTime(displaySwitchTime)) {
          setViewMode('present');
        }
      }
    };

    init();
  }, [user?.kindergartenPublicId, performMorningSync]);

  // בסימולציה: מעבר אוטומטי בין pending/present לפי displaySwitchTime
  // מעבר חד-פעמי בלבד: כשהשעה חוצה את displaySwitchTime — מעבר אחד, ואז עוצר
  // כשהסימולציה מתחילה בשעה מוקדמת (07:00) — חזרה ל-pending (חד-פעמי)
  // כשהשעה עוברת את displaySwitchTime (08:45) — מעבר ל-present (חד-פעמי)
  // שינוי: לא מפריע למשתמש לנווט ידנית אחרי המעבר האוטומטי
  const lastAutoSwitchRef = useRef<boolean | null>(null);
  useEffect(() => {
    if (!simStore.active) {
      lastAutoSwitchRef.current = null;
      return;
    }

    const checkSwitch = () => {
      const settings = useAttendanceStore.getState().kindergartenSettings;
      const displaySwitchTime = settings?.displaySwitchTime || '08:45';
      const pastSwitch = isPastOpeningTime(displaySwitchTime);

      // רק אם ה-state של pastSwitch השתנה מהפעם הקודמת — מעבר
      if (lastAutoSwitchRef.current !== pastSwitch) {
        lastAutoSwitchRef.current = pastSwitch;
        if (pastSwitch) {
          console.log('[TabletDashboard] Simulation: auto-switching to present (crossed displaySwitchTime)');
          setViewMode('present');
        } else {
          console.log('[TabletDashboard] Simulation: auto-switching to pending (sim reset before displaySwitchTime)');
          setViewMode('pending');
        }
      }
    };

    const t = setInterval(checkSwitch, 2000); // בדיקה כל 2 שניות
    checkSwitch(); // בדיקה מיידית
    // בדיקה נוספת אחרי 500ms — למקרה שה-simStore עדיין לא מסונכרן
    const delayed = setTimeout(checkSwitch, 500);
    return () => { clearInterval(t); clearTimeout(delayed); };
  }, [simStore.active]);

  // סנכרון כשחוזרים לטאב (visibility change)
  // כולל: המתנה לרשת, חידוש טוקן פרואקטיבי, סנכרון מלא/דלתא
  useEffect(() => {
    if (!user?.kindergartenPublicId) return;

    const handleVisibilityChange = async () => {
      if (document.visibilityState !== 'visible') return;

      console.log('[TabletDashboard] Tab became visible. Online:', navigator.onLine);

      // המתנה קצרה ליציבות רשת אחרי יציאה ממצב שינה
      await new Promise(resolve => setTimeout(resolve, 1000));

      if (!navigator.onLine) {
        console.log('[TabletDashboard] Offline after wake, waiting for connectivity...');
        return; // האירוע 'online' יטפל בסנכרון כשהרשת תחזור
      }

      // חידוש טוקן פרואקטיבי לפני סנכרון
      const refreshed = await proactiveTokenRefresh();
      if (!refreshed) {
        console.warn('[TabletDashboard] Token refresh failed after wake, sync may fail with 401');
      }

      // סנכרון
      const currentChildren = useAttendanceStore.getState().children;
      const needsFull = currentChildren.length === 0 || useAttendanceStore.getState().needsFullSync();
      if (needsFull) {
        console.log('[TabletDashboard] Performing full sync after wake...');
        performMorningSync(user.kindergartenPublicId!);
      } else {
        console.log('[TabletDashboard] Performing delta sync after wake...');
        performDeltaSync(user.kindergartenPublicId!);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [user?.kindergartenPublicId, performMorningSync, performDeltaSync]);

  // מעקב אחרי מצב רשת (online/offline) + סנכרון אוטומטי בחזרה לרשת
  useEffect(() => {
    if (!user?.kindergartenPublicId) return;

    const handleOnline = () => {
      console.log('[TabletDashboard] Network came online');
      setIsOnline(true);
      // סנכרון כשחוזרים לרשת
      const state = useAttendanceStore.getState();
      if (state.children.length === 0 || state.needsFullSync()) {
        performMorningSync(user.kindergartenPublicId!);
      } else {
        performDeltaSync(user.kindergartenPublicId!);
      }
    };

    const handleOffline = () => {
      console.log('[TabletDashboard] Network went offline');
      setIsOnline(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [user?.kindergartenPublicId, performMorningSync, performDeltaSync]);

  // Heartbeat: כל 5 דקות - חידוש טוקן פרואקטיבי + בדיקת תקינות
  useEffect(() => {
    if (!user?.kindergartenPublicId) return;

    const heartbeatInterval = setInterval(async () => {
      if (!navigator.onLine) return;

      // חידוש טוקן אם עומד לפוג
      await proactiveTokenRefresh();

      // בדיקת תקינות חיבור
      try {
        await syncApi.healthCheck();
      } catch {
        console.warn('[Heartbeat] Health check failed');
      }
    }, 5 * 60 * 1000); // כל 5 דקות

    return () => clearInterval(heartbeatInterval);
  }, [user?.kindergartenPublicId]);

  // האזנה לאירוע תפוגת אימות - ניווט חזרה למסך כניסה
  useEffect(() => {
    const handleAuthExpired = () => {
      console.log('[TabletDashboard] Auth expired, redirecting to login...');
      navigate('/tablet/login');
    };

    window.addEventListener('kipikid-tablet-auth-expired', handleAuthExpired);
    return () => window.removeEventListener('kipikid-tablet-auth-expired', handleAuthExpired);
  }, [navigate]);

  // סנכרון חכם - לפי מצב הילדים וזמן מאז פעילות אחרונה
  // 15 דקות אם לא כולם הגיעו, 30 דקות אם כולם הגיעו
  useEffect(() => {
    if (!user?.kindergartenPublicId) return;

    // פונקציה שמחשבת את זמן הסנכרון הבא
    const getNextSyncInterval = () => {
      const state = useAttendanceStore.getState();
      const allArrived = state.allChildrenArrived();
      const timeSinceActivity = Date.now() - state.lastActivityTime;

      // Stop periodic sync after latestArrivalTime + 30min if all children arrived
      const settings = state.kindergartenSettings;
      if (allArrived && settings?.displaySwitchTime) {
        const now = useSimulationStore.getState().getDisplayTime();
        const [h, m] = settings.displaySwitchTime.split(':').map(Number);
        const switchMinutes = h * 60 + m + STOP_SYNC_AFTER_LATEST_ARRIVAL_MINUTES;
        const nowMinutes = now.getHours() * 60 + now.getMinutes();
        if (nowMinutes > switchMinutes) {
          console.log('[TabletDashboard] All arrived + past latestArrival+30min — stopping periodic sync');
          return -1; // signal to stop
        }
      }

      // אם עברו 5 דקות ללא פעילות - סנכרון מיידי
      if (timeSinceActivity >= SYNC_AFTER_INACTIVITY) {
        return 0; // sync now
      }

      // אחרת - לפי מצב הילדים
      const base = allArrived ? SYNC_INTERVAL_ALL_ARRIVED : SYNC_INTERVAL_NOT_ALL_ARRIVED;
      // בסימולציה: קיצור interval — מינימום 30 שניות אמיתיות
      const simState = useSimulationStore.getState();
      if (simState.active && simState.ratio > 1) {
        return Math.max(30_000, Math.floor(base / simState.ratio));
      }
      return base;
    };

    // סנכרון תקופתי - לא מיד, רק אחרי interval
    const scheduleNextSync = () => {
      const interval = getNextSyncInterval();

      if (interval === -1) {
        // All arrived + past latestArrivalTime + 30min — stop syncing
        console.log('[TabletDashboard] Periodic sync stopped (all arrived, past cutoff time)');
        return;
      }

      if (interval === 0) {
        // סנכרון מיידי (כי עברו 15 דקות ללא פעילות)
        console.log('[TabletDashboard] 15 minutes of inactivity - performing delta sync...');
        performDeltaSync(user.kindergartenPublicId!);
        // תזמון הסנכרון הבא
        syncIntervalRef.current = setTimeout(scheduleNextSync, SYNC_INTERVAL_NOT_ALL_ARRIVED);
      } else {
        const allArrived = useAttendanceStore.getState().allChildrenArrived();
        console.log(`[TabletDashboard] Scheduling next sync in ${interval / 60000} minutes (allArrived: ${allArrived})`);

        syncIntervalRef.current = setTimeout(() => {
          const currentChildren = useAttendanceStore.getState().children;
          if (currentChildren.length > 0) {
            console.log('[TabletDashboard] Periodic delta sync...');
            performDeltaSync(user.kindergartenPublicId!);
          }
          // תזמון הסנכרון הבא
          scheduleNextSync();
        }, interval);
      }
    };

    // התחלת מחזור הסנכרון
    scheduleNextSync();

    return () => {
      if (syncIntervalRef.current) {
        clearTimeout(syncIntervalRef.current);
      }
    };
  }, [user?.kindergartenPublicId, performDeltaSync]);

  // איפוס טיימר מעבר אוטומטי כשיש פעילות
  const resetActivityTimer = useCallback(() => {
    setLastActivityTime(Date.now());
  }, []);

  // מעקב אחרי פעילות במסך
  useEffect(() => {
    const handleActivity = () => resetActivityTimer();
    window.addEventListener('click', handleActivity);
    window.addEventListener('touchstart', handleActivity);
    window.addEventListener('keydown', handleActivity);

    return () => {
      window.removeEventListener('click', handleActivity);
      window.removeEventListener('touchstart', handleActivity);
      window.removeEventListener('keydown', handleActivity);
    };
  }, [resetActivityTimer]);

  // ניהול מעבר אוטומטי בין מסכים - רק מ-pending ל-present
  // (לא הפוך - אם המשתמש בחר "נמצאים בגן" הוא ישאר שם)
  useEffect(() => {
    if (autoSwitchEnabled && viewMode === 'pending') {
      autoSwitchIntervalRef.current = setInterval(() => {
        const timeSinceActivity = Date.now() - lastActivityTime;
        if (timeSinceActivity >= AUTO_SWITCH_DELAY) {
          setViewMode('present');
          setAutoSwitchEnabled(false);
        }
      }, 1000);

      return () => {
        if (autoSwitchIntervalRef.current) {
          clearInterval(autoSwitchIntervalRef.current);
        }
      };
    } else if (autoSwitchEnabled && viewMode === 'present') {
      // אם אנחנו כבר ב-present, לא צריך מעבר אוטומטי
      setAutoSwitchEnabled(false);
    }
  }, [autoSwitchEnabled, lastActivityTime, viewMode]);

  // ===============================================
  // Handlers
  // ===============================================

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2500);
  };

  const handleMarkArrival = async (child: AttendanceChild) => {
    if (child.status !== 'expected') return;
    setLoadingChildId(child.publicId);
    resetActivityTimer();

    const result = await markArrival(child.publicId, user!.kindergartenPublicId!);

    if (result.success) {
      const genderText = child.gender === 2 ? 'נרשמה כנוכחת' : 'נרשם כנוכח';
      showToast(`✓ ${child.firstName} ${genderText}`);

      // אם יש עדכונים - נסנכרן
      if (result.hasUpdates) {
        performDeltaSync(user!.kindergartenPublicId!);
      }
    } else {
      showToast(`✗ שגיאה בסימון הגעה`);
    }

    setLoadingChildId(null);
    setSelectedChild(null);
  };

  const handleCancelArrival = async (child: AttendanceChild) => {
    if (child.status !== 'arrived') return;
    setLoadingChildId(child.publicId);
    resetActivityTimer();

    const success = await cancelArrival(child.publicId, user!.kindergartenPublicId!);

    if (success) {
      const genderText = child.gender === 2 ? 'הוחזרה' : 'הוחזר';
      showToast(`↩ ${child.firstName} ${genderText} לטרם הגיעו`);
    } else {
      showToast(`✗ שגיאה בביטול הגעה`);
    }

    setLoadingChildId(null);
    setSelectedChild(null);
  };

  // בחירת ילד לפתיחת חלונית
  const handleSelectChild = (child: AttendanceChild) => {
    resetActivityTimer();
    setSelectedChild(child);
  };

  // סגירת חלונית
  const handleCloseModal = () => {
    setSelectedChild(null);
  };

  // ===============================================
  // Message Handlers
  // ===============================================

  // כל הילדים (לבחירת ילד להודעה)
  const allChildren = useMemo(() => {
    return [...children].sort((a, b) => a.firstName.localeCompare(b.firstName, 'he'));
  }, [children]);

  // סינון ילדים בבחירה להודעה
  const filteredMessageChildren = useMemo(() => {
    if (!messageSearchQuery.trim()) return allChildren;
    const query = messageSearchQuery.trim().toLowerCase();
    return allChildren.filter(c =>
      c.firstName.toLowerCase().includes(query) ||
      c.lastName.toLowerCase().includes(query) ||
      `${c.firstName} ${c.lastName}`.toLowerCase().includes(query)
    );
  }, [allChildren, messageSearchQuery]);

  // סינון הודעות שנשלחו
  const filteredSentMessages = useMemo(() => {
    let result = sentMessages;

    if (msgSearchChild.trim()) {
      const q = msgSearchChild.trim().toLowerCase();
      result = result.filter(m =>
        m.sentTo === 'all' ? 'כללי'.includes(q) : (m.childName || '').toLowerCase().includes(q)
      );
    }

    if (msgSearchText.trim()) {
      const q = msgSearchText.trim().toLowerCase();
      result = result.filter(m => m.content.toLowerCase().includes(q));
    }

    if (msgDateFrom) {
      const from = new Date(msgDateFrom);
      from.setHours(0, 0, 0, 0);
      result = result.filter(m => new Date(m.sentAt) >= from);
    }
    if (msgDateTo) {
      const to = new Date(msgDateTo);
      to.setHours(23, 59, 59, 999);
      result = result.filter(m => new Date(m.sentAt) <= to);
    }

    return result;
  }, [sentMessages, msgSearchChild, msgSearchText, msgDateFrom, msgDateTo]);

  // סגירת תפריט הודעות
  const closeMessageMenu = () => {
    setMessageMenuMode('closed');
    setMessageChild(null);
    setMessageSearchQuery('');
    setSelectedTemplateId(null);
    setCustomMessageText('');
    setFinalMessageText('');
    setEditingTemplate(null);
    setNewTemplateName('');
    setNewTemplateContent('');
    setNewTemplateType('child');
    setViewMessagesChild(null);
    setSendToMobile(false); // איפוס checkbox SMS
    setMsgSearchChild('');
    setMsgSearchText('');
    setMsgDateFrom('');
    setMsgDateTo('');
  };

  // בחירת ילד להודעה פרטנית
  const handleSelectChildForMessage = (child: AttendanceChild) => {
    setMessageChild(child);
    setMessageMenuMode('compose-child');
  };

  // בחירת תבנית
  const handleSelectTemplate = (templateId: string) => {
    setSelectedTemplateId(templateId);
    setCustomMessageText(''); // איפוס טקסט חופשי
  };

  // הכנת טקסט סופי לשליחה - משתמש בטקסט הנערך (customMessageText)
  const prepareMessageForConfirmation = () => {
    // תמיד משתמשים בטקסט הנערך, לא בתבנית המקורית
    const text = customMessageText.trim();

    if (text) {
      setFinalMessageText(text);
      setMessageMenuMode('confirm');
    }
  };

  // שליחת הודעה
  const handleSendMessage = async () => {
    if (!finalMessageText || !user?.kindergartenPublicId) return;

    setSendingMessage(true);
    const result = await sendMessage({
      childPublicId: messageChild?.publicId,
      childName: messageChild ? `${messageChild.firstName} ${messageChild.lastName}` : undefined,
      templateId: selectedTemplateId || undefined,
      content: finalMessageText,
      kindergartenPublicId: user.kindergartenPublicId,
      sendToMobile: sendToMobile, // שליחת SMS לנייד
    });

    setSendingMessage(false);

    if (result.success) {
      const smsNote = sendToMobile ? ' + SMS' : '';
      showToast(messageChild ? `✓ ההודעה נשלחה להורי ${messageChild.firstName}${smsNote}` : `✓ ההודעה נשלחה לכל ההורים${smsNote}`);
      closeMessageMenu();
    } else {
      showToast(`✗ ${result.error || 'שגיאה בשליחה'}`);
    }
  };

  // שמירת תבנית חדשה
  const handleSaveNewTemplate = () => {
    if (newTemplateName.trim() && newTemplateContent.trim()) {
      if (editingTemplate) {
        updateTemplate(editingTemplate.id, newTemplateName.trim(), newTemplateContent.trim(), newTemplateType);
      } else {
        addTemplate(newTemplateName.trim(), newTemplateContent.trim(), newTemplateType);
      }
      setEditingTemplate(null);
      setNewTemplateName('');
      setNewTemplateContent('');
      setNewTemplateType('child');
    }
  };

  // בחירת ילד לצפייה בהודעות
  const handleSelectChildForViewMessages = (child: AttendanceChild) => {
    setViewMessagesChild(child);
    setMessageMenuMode('view-child-messages');
  };

  // החלפה ידנית בין מסכים עם טיימר
  const toggleViewMode = () => {
    resetActivityTimer();
    setViewMode(prev => prev === 'pending' ? 'present' : 'pending');
    setAutoSwitchEnabled(true);
  };

  // סינון ומיון ילדים
  const filteredAndSortedChildren = useMemo(() => {
    let result = viewMode === 'pending' ? expectedChildren : arrivedChildren;

    // סינון לפי חיפוש
    if (searchQuery.trim()) {
      const query = searchQuery.trim().toLowerCase();
      result = result.filter(c =>
        c.firstName.toLowerCase().includes(query) ||
        c.lastName.toLowerCase().includes(query) ||
        `${c.firstName} ${c.lastName}`.toLowerCase().includes(query)
      );
    }

    // מיון
    if (viewMode === 'pending') {
      // טרם הגיעו - מיון לפי שעת הגעה אחרונה (מאוחרים בסוף)
      result = [...result].sort((a, b) =>
        timeToMinutes(a.lastArrivalTime || a.expectedArrivalTime) -
        timeToMinutes(b.lastArrivalTime || b.expectedArrivalTime)
      );
    } else {
      // נמצאים בגן - מיון לפי א"ב (שם פרטי)
      result = [...result].sort((a, b) =>
        a.firstName.localeCompare(b.firstName, 'he')
      );
    }

    return result;
  }, [expectedChildren, arrivedChildren, viewMode, searchQuery]);

  // ===============================================
  // Render
  // ===============================================

  if (!user) return null;

  // צבעי רקע לפי מסך
  const backgroundColor = viewMode === 'pending' ? '#FFF8E1' : '#E8F5E9';
  const headerAccentColor = viewMode === 'pending' ? '#FF9800' : '#4CAF50';

  return (
    <div
      dir="rtl"
      style={{
        minHeight: '100vh',
        background: backgroundColor,
        transition: 'background-color 0.3s ease',
        paddingTop: simStore.active ? '36px' : 0,
      }}
    >
      {/* Header - כולל הכל בשורה אחת */}
      <header style={{
        background: 'white',
        padding: '8px 16px',
        boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
        position: 'sticky',
        top: 0,
        zIndex: 100,
        borderBottom: `3px solid ${headerAccentColor}`,
      }}>
        <div style={{
          maxWidth: '1600px',
          margin: '0 auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '8px 12px',
          flexWrap: 'wrap',
        }}>
          {/* לוגו ושם */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: '120px' }}>
            <img
              src="/kipikid-logo.png"
              alt="KipiKid"
              style={{ width: '36px', height: '36px', objectFit: 'contain' }}
              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
            />
            <div>
              <div style={{ fontWeight: 'bold', color: '#00BCD4', fontSize: '14px' }}>
                {user.kindergartenName || 'גן ילדים'}
              </div>
              <div style={{ fontSize: '10px', color: '#999' }}>קוד: {user.kindergartenCode}</div>
            </div>
          </div>

          {/* כותרת מסך */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            color: headerAccentColor,
            fontWeight: 'bold',
            fontSize: '18px',
          }}>
            {viewMode === 'pending' ? '🕐 טרם הגיעו' : '✓ נמצאים בגן'}
            <span style={{
              fontSize: '14px',
              color: '#666',
              fontWeight: 'normal',
            }}>
              ({filteredAndSortedChildren.length})
            </span>
          </div>

          {/* שדה חיפוש */}
          <div style={{ position: 'relative' }}>
            <input
              type="text"
              placeholder="🔍 חיפוש..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                padding: '8px 12px',
                paddingLeft: searchQuery ? '28px' : '12px',
                borderRadius: '20px',
                border: '2px solid #e0e0e0',
                fontSize: '13px',
                width: '150px',
                outline: 'none',
              }}
              onFocus={(e) => e.target.style.borderColor = headerAccentColor}
              onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                style={{
                  position: 'absolute',
                  left: '8px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  fontSize: '14px',
                  cursor: 'pointer',
                  color: '#999',
                }}
              >
                ✕
              </button>
            )}
          </div>

          {/* כפתור מעבר בין מסכים */}
          <button
            onClick={toggleViewMode}
            style={{
              padding: '8px 16px',
              background: viewMode === 'pending'
                ? 'linear-gradient(135deg, #4CAF50, #8BC34A)'
                : 'linear-gradient(135deg, #FF9800, #FFB74D)',
              border: 'none',
              borderRadius: '20px',
              color: 'white',
              fontSize: '13px',
              fontWeight: 'bold',
              cursor: 'pointer',
              boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
              whiteSpace: 'nowrap',
            }}
          >
            {viewMode === 'pending' ? 'נמצאים בגן ←' : 'טרם הגיעו ←'}
          </button>

          {/* כפתור קניות */}
          <button
            onClick={() => navigate('/tablet/purchases')}
            style={{
              padding: '8px 16px',
              background: 'linear-gradient(135deg, #16a34a, #22c55e)',
              border: 'none',
              borderRadius: '20px',
              color: 'white',
              fontSize: '13px',
              fontWeight: 'bold',
              cursor: 'pointer',
              boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
              whiteSpace: 'nowrap' as const,
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            <span style={{ fontSize: '16px' }}>🛒</span>
            קניות
          </button>

          {/* כפתור הודעות */}
          <button
            onClick={() => setMessageMenuMode('menu')}
            style={{
              padding: '8px 16px',
              background: 'linear-gradient(135deg, #2196F3, #42A5F5)',
              border: 'none',
              borderRadius: '20px',
              color: 'white',
              fontSize: '13px',
              fontWeight: 'bold',
              cursor: 'pointer',
              boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
              whiteSpace: 'nowrap',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            <span style={{ fontSize: '16px' }}>✉</span>
            הודעות
          </button>

          {/* שעון */}
          <div style={{ textAlign: 'center', minWidth: '80px' }}>
            <div style={{ fontSize: '22px', fontWeight: 'bold', color: '#00BCD4' }}>
              {time.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: false })}
            </div>
            <div style={{ fontSize: '10px', color: '#666' }}>
              {formatDate(time)}
            </div>
          </div>

          {/* עזרה */}
          <button
            onClick={helpGuide.open}
            title="מדריך עזרה (Ctrl+/)"
            style={{
              padding: '6px 10px',
              background: '#E3F2FD',
              border: 'none',
              borderRadius: '8px',
              color: '#1976D2',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 'bold',
            }}
          >
            ?
          </button>

          {/* התנתק */}
          <button
            onClick={() => { logout(); navigate('/tablet/login'); }}
            style={{
              padding: '6px 12px',
              background: '#f5f5f5',
              border: 'none',
              borderRadius: '8px',
              color: '#666',
              cursor: 'pointer',
              fontSize: '12px',
            }}
          >
            התנתק
          </button>
        </div>
      </header>

      <HelpGuideModal isOpen={helpGuide.isOpen} onClose={helpGuide.close} currentPath={helpGuide.currentPath} userRole={helpGuide.userRole} />

      {/* באנר אין חיבור לאינטרנט */}
      {!isOnline && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          background: '#FF5722',
          color: 'white',
          textAlign: 'center',
          padding: '8px',
          fontSize: '14px',
          fontWeight: 'bold',
          zIndex: 9999,
        }}>
          📡 אין חיבור לאינטרנט - נתונים עלולים להיות לא עדכניים
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed',
          top: isOnline ? '80px' : '110px',
          left: '50%',
          transform: 'translateX(-50%)',
          background: toast.includes('שגיאה') ? '#f44336' : (viewMode === 'pending' ? '#4CAF50' : '#FF9800'),
          color: 'white',
          padding: '12px 24px',
          borderRadius: '12px',
          fontWeight: 'bold',
          boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
          zIndex: 1000,
        }}>
          {toast}
        </div>
      )}

      {/* Modal - חלונית אישור */}
      {selectedChild && (
        <ChildModal
          child={selectedChild}
          viewMode={viewMode}
          loading={loadingChildId === selectedChild.publicId}
          onConfirm={() => viewMode === 'pending' ? handleMarkArrival(selectedChild) : handleCancelArrival(selectedChild)}
          onClose={handleCloseModal}
        />
      )}

      {/* הודעת יום סגור */}
      {closedDayInfo && closedDayInfo.operationMode === 0 ? (
        <div style={{
          maxWidth: '1600px',
          margin: '0 auto',
          padding: '80px 20px',
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 'calc(100vh - 120px)',
        }}>
          <div style={{ fontSize: '80px', marginBottom: '24px' }}>🏠</div>
          <div style={{
            fontSize: '32px',
            fontWeight: '700',
            color: '#374151',
            marginBottom: '16px',
          }}>
            הגן לא פועל היום
          </div>
          <div style={{
            fontSize: '22px',
            color: '#6B7280',
            marginBottom: '8px',
          }}>
            {closedDayInfo.eventName}
          </div>
          <div style={{
            fontSize: '16px',
            color: '#9CA3AF',
            marginTop: '24px',
          }}>
            {formatDate(new Date())}
          </div>
        </div>
      ) : (
      <>
      {/* רשימת ילדים */}
      <div style={{
        maxWidth: '1600px',
        margin: '0 auto',
        padding: '12px 16px',
        overflowY: 'auto',
        maxHeight: 'calc(100vh - 80px)',
      }}>
        {filteredAndSortedChildren.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '60px 20px',
            color: '#999',
          }}>
            {/* מצב מיוחד: אין ילדים כלל ב-state - בעיית סנכרון */}
            {children.length === 0 && !isSyncing ? (
              <>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>
                  {!isOnline ? '📡' : syncError?.includes('אימות') ? '🔐' : '⚠️'}
                </div>
                <div style={{ fontSize: '18px', marginBottom: '12px' }}>
                  {!isOnline
                    ? 'אין חיבור לאינטרנט'
                    : syncError?.includes('אימות')
                      ? 'נדרשת כניסה מחדש למערכת'
                      : syncError
                        ? `שגיאת סנכרון: ${syncError}`
                        : 'לא נטענו נתוני ילדים'}
                </div>

                {/* כפתור פעולה מותאם לסוג השגיאה */}
                {syncError?.includes('אימות') ? (
                  <button
                    onClick={() => {
                      useAuthStore.getState().clearAuth();
                      navigate('/tablet/login');
                    }}
                    style={{
                      padding: '12px 24px',
                      fontSize: '16px',
                      backgroundColor: '#FF5722',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontWeight: 'bold',
                    }}
                  >
                    🔑 כניסה מחדש
                  </button>
                ) : (
                  <button
                    onClick={() => user?.kindergartenPublicId && performMorningSync(user.kindergartenPublicId)}
                    disabled={!isOnline}
                    style={{
                      padding: '12px 24px',
                      fontSize: '16px',
                      backgroundColor: isOnline ? '#4CAF50' : '#ccc',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: isOnline ? 'pointer' : 'not-allowed',
                      fontWeight: 'bold',
                    }}
                  >
                    {isOnline ? '🔄 סנכרון עכשיו' : '📡 ממתין לחיבור...'}
                  </button>
                )}
              </>
            ) : children.length === 0 && isSyncing ? (
              <>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>⏳</div>
                <div style={{ fontSize: '18px' }}>מסנכרן נתונים...</div>
              </>
            ) : (
              <>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>
                  {searchQuery ? '🔍' : (viewMode === 'pending' ? '🎉' : '📭')}
                </div>
                <div style={{ fontSize: '18px' }}>
                  {searchQuery
                    ? 'לא נמצאו ילדים התואמים לחיפוש'
                    : (viewMode === 'pending' ? 'כל הילדים הגיעו!' : 'אין ילדים נוכחים')
                  }
                </div>
              </>
            )}
          </div>
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
            gap: '12px',
          }}>
            {filteredAndSortedChildren.map(child => (
              <ChildCard
                key={child.publicId}
                child={child}
                viewMode={viewMode}
                onClick={() => handleSelectChild(child)}
              />
            ))}
          </div>
        )}
      </div>
      </>
      )}

      {/* =============================================== */}
      {/* Message Menu Modal */}
      {/* =============================================== */}
      {messageMenuMode !== 'closed' && (
        <MessageOverlay onClose={closeMessageMenu}>
          {/* תפריט ראשי */}
          {messageMenuMode === 'menu' && (
            <MessagePanel title="הודעות" onClose={closeMessageMenu}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div style={{ fontWeight: 'bold', color: '#2196F3', marginBottom: '4px' }}>שליחת הודעות</div>
                <MenuButton
                  icon="👤"
                  label="הודעה לילד ספציפי"
                  description="בחר ילד ושלח הודעה להוריו"
                  onClick={() => setMessageMenuMode('select-child')}
                />
                <MenuButton
                  icon="👨‍👩‍👧‍👦"
                  label="הודעה לכל ההורים"
                  description="שלח הודעה כללית לכל הורי הגן"
                  onClick={() => setMessageMenuMode('compose-all')}
                />

                <div style={{ fontWeight: 'bold', color: '#4CAF50', marginTop: '12px', marginBottom: '4px' }}>צפייה בהודעות</div>
                <MenuButton
                  icon="📬"
                  label="אישור קבלת הודעות"
                  description="צפה בהודעות שנשלחו וסטטוס קריאה"
                  onClick={() => {
                    // טעינת הודעות מהשרת
                    if (user?.kindergartenPublicId) {
                      loadMessagesFromServer(user.kindergartenPublicId);
                    }
                    setMessageMenuMode('view-messages');
                  }}
                />

                <div style={{ fontWeight: 'bold', color: '#FF9800', marginTop: '12px', marginBottom: '4px' }}>ניהול</div>
                <MenuButton
                  icon="📝"
                  label="ניהול תבניות הודעה"
                  description="צור ועדכן תבניות הודעה"
                  onClick={() => setMessageMenuMode('templates')}
                />
              </div>
            </MessagePanel>
          )}

          {/* בחירת ילד */}
          {messageMenuMode === 'select-child' && (
            <MessagePanel
              title="בחר ילד"
              onClose={closeMessageMenu}
              onBack={() => setMessageMenuMode('menu')}
            >
              {/* חיפוש */}
              <input
                type="text"
                placeholder="🔍 חיפוש ילד..."
                value={messageSearchQuery}
                onChange={(e) => setMessageSearchQuery(e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '10px',
                  border: '2px solid #e0e0e0',
                  fontSize: '14px',
                  marginBottom: '12px',
                  boxSizing: 'border-box',
                }}
              />

              {/* רשימת ילדים */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
                gap: '10px',
                maxHeight: '400px',
                overflowY: 'auto',
              }}>
                {filteredMessageChildren.map(child => (
                  <MessageChildCard
                    key={child.publicId}
                    child={child}
                    onClick={() => handleSelectChildForMessage(child)}
                  />
                ))}
              </div>
            </MessagePanel>
          )}

          {/* כתיבת הודעה לילד */}
          {messageMenuMode === 'compose-child' && messageChild && (
            <MessagePanel
              title={`הודעה ל${messageChild.firstName} ${messageChild.lastName}`}
              onClose={closeMessageMenu}
              onBack={() => { setMessageChild(null); setMessageMenuMode('select-child'); }}
            >
              <ComposeMessage
                child={messageChild}
                templates={getTemplatesByType('child')}
                selectedTemplateId={selectedTemplateId}
                customMessageText={customMessageText}
                onSelectTemplate={handleSelectTemplate}
                onCustomTextChange={setCustomMessageText}
                onSend={prepareMessageForConfirmation}
              />
            </MessagePanel>
          )}

          {/* הודעה כללית */}
          {messageMenuMode === 'compose-all' && (
            <MessagePanel
              title="הודעה לכל ההורים"
              onClose={closeMessageMenu}
              onBack={() => setMessageMenuMode('menu')}
            >
              <ComposeMessage
                child={null}
                templates={getTemplatesByType('general')}
                selectedTemplateId={selectedTemplateId}
                customMessageText={customMessageText}
                onSelectTemplate={handleSelectTemplate}
                onCustomTextChange={setCustomMessageText}
                onSend={prepareMessageForConfirmation}
              />
            </MessagePanel>
          )}

          {/* ניהול תבניות */}
          {messageMenuMode === 'templates' && (
            <MessagePanel
              title="ניהול תבניות הודעה"
              onClose={closeMessageMenu}
              onBack={() => setMessageMenuMode('menu')}
              wide
            >
              <TemplatesManager
                templates={templates}
                editingTemplate={editingTemplate}
                newTemplateName={newTemplateName}
                newTemplateContent={newTemplateContent}
                newTemplateType={newTemplateType}
                onEditTemplate={(t) => {
                  setEditingTemplate(t);
                  setNewTemplateName(t.name);
                  setNewTemplateContent(t.content);
                  setNewTemplateType(t.type);
                }}
                onDeleteTemplate={deleteTemplate}
                onNewTemplateNameChange={setNewTemplateName}
                onNewTemplateContentChange={setNewTemplateContent}
                onNewTemplateTypeChange={setNewTemplateType}
                onSaveTemplate={handleSaveNewTemplate}
                onCancelEdit={() => {
                  setEditingTemplate(null);
                  setNewTemplateName('');
                  setNewTemplateContent('');
                  setNewTemplateType('child');
                }}
              />
            </MessagePanel>
          )}

          {/* צפייה בהודעות - בחירת ילד או הודעות כלליות */}
          {messageMenuMode === 'view-messages' && (
            <MessagePanel
              title="מעקב הודעות שנשלחו"
              onClose={closeMessageMenu}
              onBack={() => setMessageMenuMode('menu')}
              wide
            >
              {isLoadingMessages ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                  <div style={{ fontSize: '32px', marginBottom: '12px' }}>⏳</div>
                  <div>טוען הודעות מהשרת...</div>
                </div>
              ) : (
              <div>
                {/* חיפוש וסינון */}
                <div style={{ marginBottom: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {/* שורת חיפוש טקסט */}
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <input
                      type="text"
                      placeholder="🔍 חיפוש לפי שם ילד..."
                      value={msgSearchChild}
                      onChange={(e) => setMsgSearchChild(e.target.value)}
                      style={{
                        flex: 1,
                        padding: '8px 12px',
                        borderRadius: '20px',
                        border: '2px solid #e0e0e0',
                        fontSize: '13px',
                        outline: 'none',
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#2196F3'}
                      onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                    />
                    <input
                      type="text"
                      placeholder="🔍 חיפוש בתוכן ההודעה..."
                      value={msgSearchText}
                      onChange={(e) => setMsgSearchText(e.target.value)}
                      style={{
                        flex: 1,
                        padding: '8px 12px',
                        borderRadius: '20px',
                        border: '2px solid #e0e0e0',
                        fontSize: '13px',
                        outline: 'none',
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#2196F3'}
                      onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                    />
                  </div>
                  {/* שורת תאריכים */}
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <label style={{ fontSize: '12px', color: '#666', whiteSpace: 'nowrap' }}>מתאריך:</label>
                    <input
                      type="date"
                      value={msgDateFrom}
                      onChange={(e) => setMsgDateFrom(e.target.value)}
                      style={{
                        flex: 1,
                        padding: '6px 10px',
                        borderRadius: '12px',
                        border: '2px solid #e0e0e0',
                        fontSize: '13px',
                        outline: 'none',
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#2196F3'}
                      onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                    />
                    <label style={{ fontSize: '12px', color: '#666', whiteSpace: 'nowrap' }}>עד תאריך:</label>
                    <input
                      type="date"
                      value={msgDateTo}
                      onChange={(e) => setMsgDateTo(e.target.value)}
                      style={{
                        flex: 1,
                        padding: '6px 10px',
                        borderRadius: '12px',
                        border: '2px solid #e0e0e0',
                        fontSize: '13px',
                        outline: 'none',
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#2196F3'}
                      onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                    />
                    {(msgSearchChild || msgSearchText || msgDateFrom || msgDateTo) && (
                      <button
                        onClick={() => { setMsgSearchChild(''); setMsgSearchText(''); setMsgDateFrom(''); setMsgDateTo(''); }}
                        style={{
                          padding: '6px 12px',
                          borderRadius: '12px',
                          border: '1px solid #ef5350',
                          background: '#ffebee',
                          color: '#ef5350',
                          fontSize: '12px',
                          cursor: 'pointer',
                          whiteSpace: 'nowrap',
                          fontWeight: 'bold',
                        }}
                      >
                        נקה הכל
                      </button>
                    )}
                  </div>
                </div>

                {/* סה"כ הודעות */}
                <div style={{ marginBottom: '16px', padding: '12px', background: '#e3f2fd', borderRadius: '10px', textAlign: 'center' }}>
                  <span style={{ fontSize: '16px', fontWeight: 'bold', color: '#1976d2' }}>
                    {filteredSentMessages.length === sentMessages.length
                      ? `📊 סה"כ ${sentMessages.length} הודעות נשלחו`
                      : `📊 ${filteredSentMessages.length} מתוך ${sentMessages.length} הודעות`
                    }
                  </span>
                </div>

                {/* כל ההודעות */}
                <ViewMessagesPanel messages={filteredSentMessages} showChildName />
              </div>
              )}
            </MessagePanel>
          )}

          {/* צפייה בהודעות של ילד ספציפי */}
          {messageMenuMode === 'view-child-messages' && viewMessagesChild && (
            <MessagePanel
              title={`הודעות ל${viewMessagesChild.firstName} ${viewMessagesChild.lastName}`}
              onClose={closeMessageMenu}
              onBack={() => { setViewMessagesChild(null); setMessageMenuMode('view-messages'); }}
              wide
            >
              <ViewMessagesPanel
                messages={getMessagesByChild(viewMessagesChild.publicId)}
                childName={`${viewMessagesChild.firstName} ${viewMessagesChild.lastName}`}
              />
            </MessagePanel>
          )}

          {/* אישור שליחה */}
          {messageMenuMode === 'confirm' && (
            <MessagePanel
              title="אישור שליחת הודעה"
              onClose={closeMessageMenu}
              onBack={() => setMessageMenuMode(messageChild ? 'compose-child' : 'compose-all')}
            >
              <ConfirmMessage
                childName={messageChild ? `${messageChild.firstName} ${messageChild.lastName}` : null}
                messageText={finalMessageText}
                sending={sendingMessage}
                sendToMobile={sendToMobile}
                onSendToMobileChange={setSendToMobile}
                onConfirm={handleSendMessage}
                onCancel={() => setMessageMenuMode(messageChild ? 'compose-child' : 'compose-all')}
              />
            </MessagePanel>
          )}
        </MessageOverlay>
      )}
    </div>
  );
}

// ===============================================
// Message Components
// ===============================================

function MessageOverlay({ children, onClose }: { children: React.ReactNode; onClose: () => void }) {
  return (
    <div
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2000,
      }}
    >
      <div>
        {children}
      </div>
    </div>
  );
}

function MessagePanel({
  title,
  onClose,
  onBack,
  wide,
  children,
}: {
  title: string;
  onClose: () => void;
  onBack?: () => void;
  wide?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div
      dir="rtl"
      style={{
        background: 'white',
        borderRadius: '20px',
        width: wide ? '700px' : '450px',
        maxWidth: '95vw',
        maxHeight: '90vh',
        overflow: 'hidden',
        boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
        animation: 'modalPop 0.2s ease-out',
      }}
    >
      {/* Header */}
      <div style={{
        padding: '16px 20px',
        borderBottom: '1px solid #eee',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        background: 'linear-gradient(135deg, #2196F3, #42A5F5)',
        color: 'white',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {onBack && (
            <button
              onClick={onBack}
              style={{
                background: 'rgba(255,255,255,0.2)',
                border: 'none',
                borderRadius: '8px',
                padding: '6px 10px',
                color: 'white',
                cursor: 'pointer',
                fontSize: '14px',
              }}
            >
              → חזור
            </button>
          )}
          <span style={{ fontWeight: 'bold', fontSize: '18px' }}>{title}</span>
        </div>
        <button
          onClick={onClose}
          style={{
            background: 'rgba(255,255,255,0.2)',
            border: 'none',
            borderRadius: '50%',
            width: '32px',
            height: '32px',
            color: 'white',
            cursor: 'pointer',
            fontSize: '16px',
          }}
        >
          ✕
        </button>
      </div>

      {/* Content */}
      <div style={{ padding: '20px', overflowY: 'auto', maxHeight: 'calc(90vh - 70px)' }}>
        {children}
      </div>

      <style>{`
        @keyframes modalPop {
          from { opacity: 0; transform: scale(0.9); }
          to { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </div>
  );
}

function MenuButton({
  icon,
  label,
  description,
  onClick,
}: {
  icon: string;
  label: string;
  description: string;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
        padding: '16px',
        background: '#f8f9fa',
        border: '2px solid #e0e0e0',
        borderRadius: '12px',
        cursor: 'pointer',
        textAlign: 'right',
        transition: 'all 0.2s',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = '#2196F3';
        e.currentTarget.style.background = '#e3f2fd';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = '#e0e0e0';
        e.currentTarget.style.background = '#f8f9fa';
      }}
    >
      <span style={{ fontSize: '32px' }}>{icon}</span>
      <div>
        <div style={{ fontWeight: 'bold', fontSize: '16px', marginBottom: '4px' }}>{label}</div>
        <div style={{ fontSize: '12px', color: '#666' }}>{description}</div>
      </div>
    </button>
  );
}

function MessageChildCard({
  child,
  onClick,
  small,
}: {
  child: AttendanceChild;
  onClick: () => void;
  small?: boolean;
}) {
  const { getChildPhoto } = useAttendanceStore();
  const bgColor = getColor(child.firstName);
  const photoSrc = getChildPhoto(child.publicId) || child.photoUrl;

  return (
    <div
      onClick={onClick}
      style={{
        background: 'white',
        borderRadius: small ? '8px' : '10px',
        overflow: 'hidden',
        boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
        cursor: 'pointer',
        transition: 'transform 0.2s',
      }}
      onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
      onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
    >
      <div style={{
        height: small ? '60px' : '80px',
        background: photoSrc
          ? `url(${photoSrc}) center/cover no-repeat`
          : `linear-gradient(135deg, ${bgColor}, ${bgColor}99)`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        {!photoSrc && (
          <span style={{ fontSize: small ? '24px' : '36px', fontWeight: 'bold', color: 'white' }}>
            {child.firstName.charAt(0)}
          </span>
        )}
      </div>
      <div style={{
        padding: small ? '4px' : '6px',
        textAlign: 'center',
        fontSize: small ? '9px' : '11px',
        fontWeight: 'bold',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}>
        {child.firstName}
      </div>
    </div>
  );
}

// פונקציית עזר להמרת תאריך ליום בשבוע בעברית
function formatDateWithDay(date: Date): string {
  const days = ['יום ראשון', 'יום שני', 'יום שלישי', 'יום רביעי', 'יום חמישי', 'יום שישי', 'יום שבת'];
  const dayName = days[date.getDay()];
  const dateStr = date.toLocaleDateString('he-IL', { day: '2-digit', month: '2-digit', year: 'numeric' });
  return `${dateStr} ${dayName}`;
}

// פונקציה לעיבוד תבנית - החלפת placeholders
function processTemplate(content: string, childName: string | null, selectedDate?: string, selectedTime?: string): string {
  let result = content;

  // החלפת שם הילד
  if (childName) {
    result = result.replace(/{childName}/g, childName);
  }

  // החלפת תאריך - סדר חשוב! קודם הביטויים הארוכים
  if (selectedDate) {
    const date = new Date(selectedDate);
    const formattedDate = formatDateWithDay(date);
    result = result.replace(/בתאריך מחר/g, `בתאריך ${formattedDate}`);
    result = result.replace(/{date}/g, formattedDate);
    result = result.replace(/מחר/g, formattedDate);
  }

  // החלפת שעה
  if (selectedTime) {
    result = result.replace(/{time}/g, selectedTime);
    result = result.replace(/בשעה \[שעה\]/g, `בשעה ${selectedTime}`);
    result = result.replace(/\[שעה\]/g, selectedTime);
  }

  return result;
}

// בדיקה אם התבנית מכילה placeholder של תאריך
function hasDatePlaceholder(content: string): boolean {
  return content.includes('{date}') || content.includes('מחר') || content.includes('בתאריך');
}

// בדיקה אם התבנית מכילה placeholder של שעה
function hasTimePlaceholder(content: string): boolean {
  return content.includes('{time}') || content.includes('[שעה]');
}

// קומפוננטת textarea חכמה שמחליפה placeholders בסוגריים מרובעים
function MessageTextArea({
  value,
  onChange,
  placeholder,
  hasTemplate,
}: {
  value: string;
  onChange: (text: string) => void;
  placeholder: string;
  hasTemplate: boolean;
}) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // מציאת placeholder בסוגריים מרובעים במיקום מסוים
  const findPlaceholderAtPosition = (text: string, pos: number): { start: number; end: number } | null => {
    // מחפשים את כל ה-placeholders בטקסט
    const regex = /\[[^\[\]]+\]/g;
    let match;
    while ((match = regex.exec(text)) !== null) {
      const start = match.index;
      const end = start + match[0].length;
      // בודקים אם המיקום נמצא בתוך או ליד ה-placeholder
      if (pos >= start && pos <= end) {
        return { start, end };
      }
    }
    return null;
  };

  // פונקציה לטיפול בהקלדה
  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const textarea = e.target;
    const newValue = textarea.value;

    // פשוט מעדכנים את הערך
    onChange(newValue);
  };

  // פונקציה לטיפול לפני הקלדה - כאן נזהה ונחליף placeholders
  const handleBeforeInput = (e: React.FormEvent<HTMLTextAreaElement> & { data?: string }) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const selStart = textarea.selectionStart;
    const selEnd = textarea.selectionEnd;
    const text = value;

    // בדיקה אם יש טקסט נבחר (selection) שהוא חלק מ-placeholder
    // או שהקורסור נמצא בתוך placeholder
    const placeholder = findPlaceholderAtPosition(text, selStart);

    if (placeholder) {
      // בודקים אם הקורסור/selection נמצא בתוך ה-placeholder (לא על הסוגריים עצמם)
      const isInsidePlaceholder = selStart > placeholder.start && selEnd < placeholder.end;
      const isOnOpenBracket = selStart === placeholder.start;
      const isOnCloseBracket = selEnd === placeholder.end;
      const isFullySelected = selStart === placeholder.start + 1 && selEnd === placeholder.end - 1;

      // אם הקורסור בתוך ה-placeholder או שהתוכן נבחר
      if (isInsidePlaceholder || isFullySelected || (selStart > placeholder.start && selStart < placeholder.end)) {
        e.preventDefault();

        // הטקסט שהמשתמש מקליד
        const inputData = (e as any).data || '';

        // מחליפים את כל ה-placeholder בטקסט החדש
        const beforePlaceholder = text.substring(0, placeholder.start);
        const afterPlaceholder = text.substring(placeholder.end);
        const newText = beforePlaceholder + inputData + afterPlaceholder;

        onChange(newText);

        // מיקום הקורסור אחרי הטקסט שהוקלד
        setTimeout(() => {
          if (textareaRef.current) {
            const newPos = beforePlaceholder.length + inputData.length;
            textareaRef.current.setSelectionRange(newPos, newPos);
          }
        }, 0);
      }
    }
  };

  // פונקציה ללחיצה על הטקסט - בחירת placeholder שלם
  const handleClick = () => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    // נותנים לדפדפן לעדכן את המיקום קודם
    setTimeout(() => {
      const cursorPos = textarea.selectionStart;
      const text = textarea.value;

      const placeholder = findPlaceholderAtPosition(text, cursorPos);
      if (placeholder) {
        // בחירת התוכן בתוך הסוגריים (ללא הסוגריים עצמם)
        textarea.setSelectionRange(placeholder.start + 1, placeholder.end - 1);
      }
    }, 0);
  };

  return (
    <textarea
      ref={textareaRef}
      value={value}
      onChange={handleChange}
      onBeforeInput={handleBeforeInput}
      onClick={handleClick}
      placeholder={placeholder}
      style={{
        width: '100%',
        minHeight: '120px',
        padding: '14px',
        borderRadius: '12px',
        border: hasTemplate ? '2px solid #4CAF50' : '2px solid #e0e0e0',
        fontSize: '15px',
        resize: 'vertical',
        boxSizing: 'border-box',
        lineHeight: '1.6',
        background: hasTemplate ? '#f1f8e9' : 'white',
      }}
    />
  );
}

function ComposeMessage({
  child,
  templates,
  selectedTemplateId,
  customMessageText,
  onSelectTemplate,
  onCustomTextChange,
  onSend,
}: {
  child: AttendanceChild | null;
  templates: MessageTemplate[];
  selectedTemplateId: string | null;
  customMessageText: string;
  onSelectTemplate: (id: string) => void;
  onCustomTextChange: (text: string) => void;
  onSend: () => void;
}) {
  const { getChildPhoto } = useAttendanceStore();
  const photoSrc = child ? getChildPhoto(child.publicId) : child?.photoUrl;
  const bgColor = child ? getColor(child.firstName) : '#2196F3';
  const childName = child ? `${child.firstName} ${child.lastName}` : null;

  // State לתאריך ושעה
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [selectedTime, setSelectedTime] = useState<string>('');

  // בדיקה אם התבנית הנוכחית צריכה תאריך או שעה
  const currentTemplate = templates.find(t => t.id === selectedTemplateId);
  const needsDate = currentTemplate ? hasDatePlaceholder(currentTemplate.content) : false;
  const needsTime = currentTemplate ? hasTimePlaceholder(currentTemplate.content) : false;

  // כאשר בוחרים תבנית - מעבד ומעתיק לשדה הטקסט
  const handleSelectTemplate = (templateId: string) => {
    onSelectTemplate(templateId);
    const template = templates.find(t => t.id === templateId);
    if (template) {
      // ברירת מחדל - מחר לתאריך, 08:00 לשעה
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      const defaultDate = tomorrow.toISOString().split('T')[0];
      const defaultTime = '08:00';
      setSelectedDate(defaultDate);
      setSelectedTime(defaultTime);

      const processed = processTemplate(template.content, childName, defaultDate, defaultTime);
      onCustomTextChange(processed);
    }
  };

  // כאשר משנים תאריך - מעדכן את הטקסט
  const handleDateChange = (newDate: string) => {
    setSelectedDate(newDate);
    if (currentTemplate) {
      const processed = processTemplate(currentTemplate.content, childName, newDate, selectedTime);
      onCustomTextChange(processed);
    }
  };

  // כאשר משנים שעה - מעדכן את הטקסט
  const handleTimeChange = (newTime: string) => {
    setSelectedTime(newTime);
    if (currentTemplate) {
      const processed = processTemplate(currentTemplate.content, childName, selectedDate, newTime);
      onCustomTextChange(processed);
    }
  };

  return (
    <div>
      {/* כרטיס ילד (אם נבחר) */}
      {child && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          marginBottom: '20px',
          padding: '12px',
          background: '#f8f9fa',
          borderRadius: '12px',
        }}>
          <div style={{
            width: '60px',
            height: '60px',
            borderRadius: '50%',
            background: photoSrc
              ? `url(${photoSrc}) center/cover no-repeat`
              : `linear-gradient(135deg, ${bgColor}, ${bgColor}99)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            {!photoSrc && (
              <span style={{ fontSize: '24px', fontWeight: 'bold', color: 'white' }}>
                {child.firstName.charAt(0)}
              </span>
            )}
          </div>
          <div>
            <div style={{ fontWeight: 'bold', fontSize: '16px' }}>
              {child.firstName} {child.lastName}
            </div>
            <div style={{ fontSize: '12px', color: '#666' }}>
              ההודעה תשלח להורי הילד/ה
            </div>
          </div>
        </div>
      )}

      {/* בחירת תבנית */}
      <div style={{ marginBottom: '16px' }}>
        <div style={{ fontWeight: 'bold', marginBottom: '8px', fontSize: '14px' }}>
          בחר תבנית הודעה:
        </div>
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '8px',
          maxHeight: '100px',
          overflowY: 'auto',
        }}>
          {templates.map(template => (
            <button
              key={template.id}
              onClick={() => handleSelectTemplate(template.id)}
              style={{
                padding: '8px 12px',
                borderRadius: '20px',
                border: selectedTemplateId === template.id ? '2px solid #2196F3' : '2px solid #e0e0e0',
                background: selectedTemplateId === template.id ? '#e3f2fd' : 'white',
                cursor: 'pointer',
                fontSize: '12px',
                transition: 'all 0.2s',
              }}
            >
              {template.name}
            </button>
          ))}
        </div>
      </div>

      {/* בוחרי תאריך ושעה - מוצגים רק אם התבנית דורשת */}
      {(needsDate || needsTime) && (
        <div style={{
          display: 'flex',
          gap: '16px',
          marginBottom: '16px',
          padding: '12px',
          background: '#fff8e1',
          borderRadius: '10px',
          border: '1px solid #ffcc80',
        }}>
          {needsDate && (
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 'bold', color: '#e65100' }}>
                📅 בחר תאריך:
              </label>
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => handleDateChange(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                style={{
                  width: '100%',
                  padding: '10px',
                  borderRadius: '8px',
                  border: '2px solid #ffcc80',
                  fontSize: '14px',
                  boxSizing: 'border-box',
                }}
              />
            </div>
          )}
          {needsTime && (
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 'bold', color: '#e65100' }}>
                🕐 בחר שעה:
              </label>
              <input
                type="time"
                value={selectedTime}
                onChange={(e) => handleTimeChange(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px',
                  borderRadius: '8px',
                  border: '2px solid #ffcc80',
                  fontSize: '14px',
                  boxSizing: 'border-box',
                }}
              />
            </div>
          )}
        </div>
      )}

      {/* שדה טקסט לעריכה - תצוגה מקדימה ניתנת לעריכה */}
      <div style={{ marginBottom: '16px' }}>
        <div style={{ fontWeight: 'bold', marginBottom: '8px', fontSize: '14px' }}>
          {selectedTemplateId ? 'ערוך את ההודעה:' : 'כתוב הודעה:'}
        </div>
        <MessageTextArea
          value={customMessageText}
          onChange={onCustomTextChange}
          placeholder="בחר תבנית או כתוב הודעה חופשית..."
          hasTemplate={!!selectedTemplateId}
        />
        <div style={{ fontSize: '11px', color: '#999', marginTop: '4px' }}>
          💡 לחץ על [טקסט בסוגריים] כדי להחליף אותו בטקסט שלך
        </div>
      </div>

      {/* כפתור שליחה */}
      <button
        onClick={onSend}
        disabled={!selectedTemplateId && !customMessageText.trim()}
        style={{
          width: '100%',
          padding: '14px',
          borderRadius: '12px',
          border: 'none',
          background: (!selectedTemplateId && !customMessageText.trim())
            ? '#ccc'
            : 'linear-gradient(135deg, #4CAF50, #8BC34A)',
          color: 'white',
          fontSize: '16px',
          fontWeight: 'bold',
          cursor: (!selectedTemplateId && !customMessageText.trim()) ? 'not-allowed' : 'pointer',
        }}
      >
        המשך לאישור →
      </button>
    </div>
  );
}

function TemplatesManager({
  templates,
  editingTemplate,
  newTemplateName,
  newTemplateContent,
  newTemplateType,
  onEditTemplate,
  onDeleteTemplate,
  onNewTemplateNameChange,
  onNewTemplateContentChange,
  onNewTemplateTypeChange,
  onSaveTemplate,
  onCancelEdit,
}: {
  templates: MessageTemplate[];
  editingTemplate: MessageTemplate | null;
  newTemplateName: string;
  newTemplateContent: string;
  newTemplateType: TemplateType;
  onEditTemplate: (t: MessageTemplate) => void;
  onDeleteTemplate: (id: string) => void;
  onNewTemplateNameChange: (name: string) => void;
  onNewTemplateContentChange: (content: string) => void;
  onNewTemplateTypeChange: (type: TemplateType) => void;
  onSaveTemplate: () => void;
  onCancelEdit: () => void;
}) {
  // הפרדה לפי סוג
  const childTemplates = templates.filter(t => t.type === 'child');
  const generalTemplates = templates.filter(t => t.type === 'general');

  return (
    <div style={{ display: 'flex', gap: '20px' }}>
      {/* רשימת תבניות */}
      <div style={{ flex: 1 }}>
        {/* תבניות לילד */}
        <div style={{ fontWeight: 'bold', marginBottom: '8px', fontSize: '14px', color: '#4CAF50' }}>
          👤 תבניות לילד ספציפי:
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', maxHeight: '150px', overflowY: 'auto', marginBottom: '16px' }}>
          {childTemplates.map(template => (
            <TemplateItem
              key={template.id}
              template={template}
              isEditing={editingTemplate?.id === template.id}
              onEdit={() => onEditTemplate(template)}
              onDelete={() => onDeleteTemplate(template.id)}
            />
          ))}
        </div>

        {/* תבניות כלליות */}
        <div style={{ fontWeight: 'bold', marginBottom: '8px', fontSize: '14px', color: '#2196F3' }}>
          📢 תבניות כלליות לכל הגן:
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', maxHeight: '150px', overflowY: 'auto' }}>
          {generalTemplates.map(template => (
            <TemplateItem
              key={template.id}
              template={template}
              isEditing={editingTemplate?.id === template.id}
              onEdit={() => onEditTemplate(template)}
              onDelete={() => onDeleteTemplate(template.id)}
            />
          ))}
        </div>
      </div>

      {/* טופס יצירה/עריכה */}
      <div style={{ flex: 1, borderRight: '1px solid #eee', paddingRight: '20px' }}>
        <div style={{ fontWeight: 'bold', marginBottom: '12px', fontSize: '14px' }}>
          {editingTemplate ? 'עריכת תבנית' : 'יצירת תבנית חדשה'}
        </div>

        {/* בחירת סוג תבנית */}
        <div style={{ marginBottom: '12px' }}>
          <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', color: '#666' }}>
            סוג התבנית:
          </label>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={() => onNewTemplateTypeChange('child')}
              style={{
                flex: 1,
                padding: '10px',
                borderRadius: '8px',
                border: newTemplateType === 'child' ? '2px solid #4CAF50' : '2px solid #e0e0e0',
                background: newTemplateType === 'child' ? '#e8f5e9' : 'white',
                cursor: 'pointer',
                fontSize: '13px',
              }}
            >
              👤 לילד ספציפי
            </button>
            <button
              onClick={() => onNewTemplateTypeChange('general')}
              style={{
                flex: 1,
                padding: '10px',
                borderRadius: '8px',
                border: newTemplateType === 'general' ? '2px solid #2196F3' : '2px solid #e0e0e0',
                background: newTemplateType === 'general' ? '#e3f2fd' : 'white',
                cursor: 'pointer',
                fontSize: '13px',
              }}
            >
              📢 כללית לכל הגן
            </button>
          </div>
        </div>

        <div style={{ marginBottom: '12px' }}>
          <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', color: '#666' }}>
            שם התבנית:
          </label>
          <input
            type="text"
            value={newTemplateName}
            onChange={(e) => onNewTemplateNameChange(e.target.value)}
            placeholder="לדוגמה: הודעת חירום"
            style={{
              width: '100%',
              padding: '10px',
              borderRadius: '8px',
              border: '2px solid #e0e0e0',
              fontSize: '14px',
              boxSizing: 'border-box',
            }}
          />
        </div>

        <div style={{ marginBottom: '12px' }}>
          <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', color: '#666' }}>
            תוכן ההודעה:
            {newTemplateType === 'child' && (
              <span style={{ color: '#4CAF50', marginRight: '8px' }}>
                (השתמש ב-{'{childName}'} לשם הילד)
              </span>
            )}
          </label>
          <textarea
            value={newTemplateContent}
            onChange={(e) => onNewTemplateContentChange(e.target.value)}
            placeholder={newTemplateType === 'child'
              ? "לדוגמה: שלום, רצינו לעדכן על {childName}..."
              : "לדוגמה: שלום לכל ההורים, ..."
            }
            style={{
              width: '100%',
              minHeight: '100px',
              padding: '10px',
              borderRadius: '8px',
              border: '2px solid #e0e0e0',
              fontSize: '14px',
              resize: 'vertical',
              boxSizing: 'border-box',
            }}
          />
        </div>

        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            onClick={onSaveTemplate}
            disabled={!newTemplateName.trim() || !newTemplateContent.trim()}
            style={{
              flex: 1,
              padding: '12px',
              borderRadius: '10px',
              border: 'none',
              background: (!newTemplateName.trim() || !newTemplateContent.trim())
                ? '#ccc'
                : 'linear-gradient(135deg, #4CAF50, #8BC34A)',
              color: 'white',
              fontSize: '14px',
              fontWeight: 'bold',
              cursor: (!newTemplateName.trim() || !newTemplateContent.trim()) ? 'not-allowed' : 'pointer',
            }}
          >
            {editingTemplate ? 'עדכן תבנית' : 'שמור תבנית'}
          </button>
          {editingTemplate && (
            <button
              onClick={onCancelEdit}
              style={{
                padding: '12px 20px',
                borderRadius: '10px',
                border: '2px solid #e0e0e0',
                background: 'white',
                cursor: 'pointer',
                fontSize: '14px',
              }}
            >
              ביטול
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function TemplateItem({
  template,
  isEditing,
  onEdit,
  onDelete,
}: {
  template: MessageTemplate;
  isEditing: boolean;
  onEdit: () => void;
  onDelete: () => void;
}) {
  return (
    <div
      style={{
        padding: '10px',
        background: isEditing ? '#e3f2fd' : '#f8f9fa',
        borderRadius: '8px',
        border: isEditing ? '2px solid #2196F3' : '2px solid transparent',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2px' }}>
        <span style={{ fontWeight: 'bold', fontSize: '12px' }}>
          {template.name}
          {template.isDefault && (
            <span style={{ fontSize: '9px', color: '#666', marginRight: '4px' }}>(ברירת מחדל)</span>
          )}
        </span>
        {!template.isDefault && (
          <div style={{ display: 'flex', gap: '4px' }}>
            <button
              onClick={onEdit}
              style={{
                padding: '3px 6px',
                background: '#2196F3',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '10px',
              }}
            >
              ערוך
            </button>
            <button
              onClick={onDelete}
              style={{
                padding: '3px 6px',
                background: '#f44336',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '10px',
              }}
            >
              מחק
            </button>
          </div>
        )}
      </div>
      <div style={{ fontSize: '10px', color: '#666', lineHeight: '1.3' }}>
        {template.content.substring(0, 60)}{template.content.length > 60 ? '...' : ''}
      </div>
    </div>
  );
}

// פונקציית עזר לעיצוב תאריך ושעה
function formatDateTime(isoString: string): { date: string; time: string } {
  const d = new Date(isoString);
  const date = d.toLocaleDateString('he-IL', { day: '2-digit', month: '2-digit', year: 'numeric' });
  const time = d.toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' });
  return { date, time };
}

function ViewMessagesPanel({
  messages,
  childName,
  showChildName = false,
}: {
  messages: SentMessage[];
  childName?: string;
  showChildName?: boolean;
}) {
  const [expandedMessageId, setExpandedMessageId] = useState<string | null>(null);

  if (messages.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
        <div style={{ fontSize: '48px', marginBottom: '12px' }}>📭</div>
        <div>אין הודעות {childName ? `ל${childName}` : ''}</div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', maxHeight: '500px', overflowY: 'auto' }}>
      {messages.map(msg => {
        const { date, time } = formatDateTime(msg.sentAt);
        const readCount = msg.readStatus.filter(s => s.readAt).length;
        const totalCount = msg.readStatus.length;
        const allRead = readCount === totalCount && totalCount > 0;
        const isExpanded = expandedMessageId === msg.id;
        const isGeneral = msg.sentTo === 'all';

        // מיון: נקראו קודם (לפי א"ב), אחר כך לא נקראו (לפי א"ב)
        const sortedReadStatus = [...msg.readStatus].sort((a, b) => {
          // קודם לפי נקרא/לא נקרא
          if (a.readAt && !b.readAt) return -1;
          if (!a.readAt && b.readAt) return 1;
          // אחר כך לפי א"ב
          return a.parentName.localeCompare(b.parentName, 'he');
        });

        return (
          <div
            key={msg.id}
            style={{
              padding: '12px',
              background: isGeneral ? '#e3f2fd' : '#f8f9fa',
              borderRadius: '10px',
              borderRight: `4px solid ${allRead ? '#4CAF50' : '#FF9800'}`,
            }}
          >
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <div style={{ fontSize: '12px', color: '#666' }}>
                {showChildName && (
                  <span style={{
                    fontWeight: 'bold',
                    color: isGeneral ? '#1976d2' : '#388e3c',
                    marginLeft: '8px',
                  }}>
                    {isGeneral ? '📢 כללי' : `👤 ${msg.childName || 'ילד'}`}
                  </span>
                )}
                📅 {date} | 🕐 {time}
              </div>
              <div style={{
                fontSize: '11px',
                padding: '2px 8px',
                borderRadius: '10px',
                background: allRead ? '#e8f5e9' : '#fff3e0',
                color: allRead ? '#4CAF50' : '#FF9800',
                fontWeight: 'bold',
              }}>
                {totalCount === 0 ? 'אין נמענים' : (allRead ? '✓ כולם קראו' : `${readCount}/${totalCount} קראו`)}
              </div>
            </div>

            {/* תוכן ההודעה - מקוצר */}
            <div style={{
              fontSize: '13px',
              lineHeight: '1.5',
              marginBottom: '10px',
              padding: '8px',
              background: 'white',
              borderRadius: '6px',
            }}>
              {isExpanded
                ? msg.content
                : (msg.content.length > 80 ? msg.content.substring(0, 80) + '...' : msg.content)
              }
            </div>

            {/* כפתור צפייה באישורי קריאה */}
            <button
              onClick={() => setExpandedMessageId(isExpanded ? null : msg.id)}
              style={{
                width: '100%',
                padding: '8px',
                borderRadius: '8px',
                border: '1px solid #e0e0e0',
                background: isExpanded ? '#e3f2fd' : 'white',
                cursor: 'pointer',
                fontSize: '12px',
                color: '#2196F3',
                fontWeight: 'bold',
              }}
            >
              {isExpanded ? '▲ הסתר אישורי קריאה' : '▼ צפה באישורי קריאה'}
            </button>

            {/* סטטוס קריאה מפורט - מוצג רק כשמורחב */}
            {isExpanded && (
              <div style={{ marginTop: '12px', fontSize: '12px' }}>
                {/* נקראו */}
                {sortedReadStatus.filter(s => s.readAt).length > 0 && (
                  <div style={{ marginBottom: '10px' }}>
                    <div style={{ fontWeight: 'bold', color: '#4CAF50', marginBottom: '6px' }}>
                      ✓ נקראו ({sortedReadStatus.filter(s => s.readAt).length})
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      {sortedReadStatus.filter(s => s.readAt).map((status, idx) => {
                        const readTime = formatDateTime(status.readAt!);
                        return (
                          <div
                            key={idx}
                            style={{
                              padding: '6px 10px',
                              borderRadius: '6px',
                              background: '#e8f5e9',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                            }}
                          >
                            <span>{status.parentName}</span>
                            <span style={{ color: '#666', fontSize: '11px' }}>
                              {readTime.date} {readTime.time}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* לא נקראו */}
                {sortedReadStatus.filter(s => !s.readAt).length > 0 && (
                  <div>
                    <div style={{ fontWeight: 'bold', color: '#f44336', marginBottom: '6px' }}>
                      ✗ טרם נקראו ({sortedReadStatus.filter(s => !s.readAt).length})
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      {sortedReadStatus.filter(s => !s.readAt).map((status, idx) => (
                        <div
                          key={idx}
                          style={{
                            padding: '6px 10px',
                            borderRadius: '6px',
                            background: '#ffebee',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                          }}
                        >
                          <span>{status.parentName}</span>
                          <span style={{ color: '#999', fontSize: '11px' }}>ממתין לקריאה</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function ConfirmMessage({
  childName,
  messageText,
  sending,
  sendToMobile,
  onSendToMobileChange,
  onConfirm,
  onCancel,
}: {
  childName: string | null;
  messageText: string;
  sending: boolean;
  sendToMobile: boolean;
  onSendToMobileChange: (value: boolean) => void;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <div>
      <div style={{
        marginBottom: '16px',
        padding: '16px',
        background: '#fff3e0',
        borderRadius: '12px',
        border: '2px solid #FF9800',
      }}>
        <div style={{ fontWeight: 'bold', marginBottom: '8px', color: '#E65100' }}>
          {childName ? `נמען: הורי ${childName}` : 'נמען: כל הורי הגן'}
        </div>
        <div style={{ fontSize: '14px', lineHeight: '1.6' }}>
          {messageText}
        </div>
      </div>

      {/* Checkbox לשליחת SMS */}
      <div style={{
        marginBottom: '16px',
        padding: '12px 16px',
        background: sendToMobile ? '#e8f5e9' : '#f5f5f5',
        borderRadius: '10px',
        border: sendToMobile ? '2px solid #4CAF50' : '2px solid #e0e0e0',
        cursor: 'pointer',
        transition: 'all 0.2s',
      }}
      onClick={() => onSendToMobileChange(!sendToMobile)}
      >
        <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={sendToMobile}
            onChange={(e) => onSendToMobileChange(e.target.checked)}
            style={{
              width: '20px',
              height: '20px',
              cursor: 'pointer',
              accentColor: '#4CAF50',
            }}
          />
          <div>
            <div style={{ fontWeight: 'bold', fontSize: '14px', color: sendToMobile ? '#2e7d32' : '#666' }}>
              📱 שלח גם SMS לנייד
            </div>
            <div style={{ fontSize: '11px', color: '#999', marginTop: '2px' }}>
              ההורים יקבלו הודעת טקסט לטלפון בנוסף להודעה באפליקציה
            </div>
          </div>
        </label>
      </div>

      <div style={{ display: 'flex', gap: '12px' }}>
        <button
          onClick={onConfirm}
          disabled={sending}
          style={{
            flex: 1,
            padding: '14px',
            borderRadius: '12px',
            border: 'none',
            background: sending ? '#ccc' : 'linear-gradient(135deg, #4CAF50, #8BC34A)',
            color: 'white',
            fontSize: '16px',
            fontWeight: 'bold',
            cursor: sending ? 'not-allowed' : 'pointer',
          }}
        >
          {sending ? 'שולח...' : (sendToMobile ? '✓ אשר ושלח + SMS' : '✓ אשר ושלח')}
        </button>
        <button
          onClick={onCancel}
          disabled={sending}
          style={{
            padding: '14px 24px',
            borderRadius: '12px',
            border: '2px solid #e0e0e0',
            background: 'white',
            cursor: sending ? 'not-allowed' : 'pointer',
            fontSize: '16px',
          }}
        >
          חזור
        </button>
      </div>
    </div>
  );
}

// ===============================================
// Child Card Component - קומפקטי, לחיצה פותחת Modal
// ===============================================

function ChildCard({
  child,
  viewMode,
  onClick,
}: {
  child: AttendanceChild;
  viewMode: ViewMode;
  onClick: () => void;
}) {
  const { getChildPhoto } = useAttendanceStore();
  const bgColor = getColor(child.firstName);
  const isPending = viewMode === 'pending';

  // תמונה - עדיפות ל-cache מקומי
  const photoSrc = getChildPhoto(child.publicId) || child.photoUrl;

  // צבע רקע רך לפי מסך
  const infoBgColor = isPending ? 'rgba(255, 152, 0, 0.15)' : 'rgba(76, 175, 80, 0.15)';

  return (
    <div
      onClick={onClick}
      style={{
        background: 'white',
        borderRadius: '12px',
        overflow: 'hidden',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        cursor: 'pointer',
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'scale(1.03)';
        e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.15)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'scale(1)';
        e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)';
      }}
    >
      {/* תמונה/אווטאר */}
      <div style={{
        height: '130px',
        background: photoSrc
          ? `url(${photoSrc}) center/cover no-repeat`
          : `linear-gradient(135deg, ${bgColor} 0%, ${bgColor}99 100%)`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
      }}>
        {!photoSrc && (
          <span style={{ fontSize: '52px', fontWeight: 'bold', color: 'white' }}>
            {child.firstName.charAt(0)}
          </span>
        )}

        {!isPending && (
          <div style={{
            position: 'absolute',
            top: '6px',
            left: '6px',
            background: 'white',
            padding: '2px 6px',
            borderRadius: '8px',
            fontSize: '9px',
            fontWeight: 'bold',
            color: '#4CAF50',
            boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
          }}>
            ✓ נוכח
          </div>
        )}
      </div>

      {/* פרטים - רקע צבעוני רך */}
      <div style={{
        padding: '6px 8px',
        background: infoBgColor,
      }}>
        <div style={{
          fontWeight: 'bold',
          fontSize: '12px',
          textAlign: 'center',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}>
          {child.firstName} {child.lastName}
        </div>

        <div style={{
          textAlign: 'center',
          fontSize: '9px',
          color: '#666',
        }}>
          {isPending
            ? (child.expectedArrivalTime ? `צפוי: ${child.expectedArrivalTime.substring(0, 5)}` : '')
            : (child.arrivalTime ? `${child.gender === 2 ? 'הגיעה' : 'הגיע'}: ${formatArrivalTime(child.arrivalTime)}` : '')
          }
          {!isPending && child.arrivalTime && child.arrivalSource === 'bletag' && ' · תג'}
        </div>
      </div>
    </div>
  );
}

// ===============================================
// Helper function for formatting arrival time
// ===============================================

// פונקציה לעיצוב שעת הגעה - חילוץ HH:mm ישירות בלי המרת timezone
// Backend שומר זמן ישראל אבל מסמן כ-UTC, לכן אסור להשתמש ב-new Date()
const formatArrivalTime = (time: string): string => {
  // אם כבר בפורמט HH:mm, החזר כמו שזה
  if (/^\d{2}:\d{2}$/.test(time)) {
    return time;
  }

  // HH:mm:ss format
  if (/^\d{2}:\d{2}:\d{2}$/.test(time)) {
    return time.substring(0, 5);
  }

  // חילוץ שעה מ-ISO או כל פורמט שמכיל HH:mm
  const match = time.match(/(\d{2}):(\d{2})/);
  if (match) {
    return `${match[1]}:${match[2]}`;
  }

  return time; // החזר כמו שזה אם לא הצלחנו לפרסר
};

// ===============================================
// Child Modal Component - חלונית אישור עם טיימר 5 שניות
// ===============================================

function ChildModal({
  child,
  viewMode,
  loading,
  onConfirm,
  onClose,
}: {
  child: AttendanceChild;
  viewMode: ViewMode;
  loading: boolean;
  onConfirm: () => void;
  onClose: () => void;
}) {
  const { getChildPhoto } = useAttendanceStore();
  const bgColor = getColor(child.firstName);
  const isPending = viewMode === 'pending';
  const photoSrc = getChildPhoto(child.publicId) || child.photoUrl;

  // שמירת onClose ב-ref כדי שהטיימר לא יתאפס בכל רינדור
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // טיימר לסגירה אוטומטית אחרי 5 שניות
  useEffect(() => {
    console.log('[Modal] Starting 5 second auto-close timer');
    const timer = setTimeout(() => {
      console.log('[Modal] Timer fired, loading:', loading);
      if (!loading) {
        console.log('[Modal] Closing modal');
        onCloseRef.current();
      }
    }, 5000);

    return () => {
      console.log('[Modal] Clearing timer');
      clearTimeout(timer);
    };
  }, [loading]);

  // בדיקת כיוון השפה
  const isRTL = document.dir === 'rtl' || document.documentElement.lang === 'he';

  return (
    <div
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
    >
      <div
        style={{
          background: 'white',
          borderRadius: '20px',
          overflow: 'hidden',
          boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
          width: '350px',
          maxWidth: '90vw',
          animation: 'modalPop 0.2s ease-out',
          position: 'relative',
        }}
      >
        {/* תמונה גדולה עם כפתור X */}
        <div style={{
          height: '280px',
          background: photoSrc
            ? `url(${photoSrc}) center/cover no-repeat`
            : `linear-gradient(135deg, ${bgColor} 0%, ${bgColor}99 100%)`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
        }}>
          {!photoSrc && (
            <span style={{ fontSize: '120px', fontWeight: 'bold', color: 'white' }}>
              {child.firstName.charAt(0)}
            </span>
          )}

          {/* כפתור X בפינה - שמאל בעברית, ימין באנגלית */}
          <button
            onClick={onClose}
            style={{
              position: 'absolute',
              top: '12px',
              [isRTL ? 'left' : 'right']: '12px',
              background: 'rgba(0,0,0,0.5)',
              border: 'none',
              borderRadius: '50%',
              width: '36px',
              height: '36px',
              color: 'white',
              fontSize: '20px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'background 0.2s',
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(0,0,0,0.7)'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(0,0,0,0.5)'}
          >
            ✕
          </button>
        </div>

        {/* פרטים */}
        <div style={{
          padding: '16px 20px 20px',
          background: isPending ? 'rgba(255, 152, 0, 0.1)' : 'rgba(76, 175, 80, 0.1)',
        }}>
          <div style={{
            fontWeight: 'bold',
            fontSize: '22px',
            textAlign: 'center',
            marginBottom: '4px',
          }}>
            {child.firstName} {child.lastName}
          </div>

          <div style={{
            textAlign: 'center',
            fontSize: '13px',
            color: '#666',
            marginBottom: '16px',
          }}>
            {isPending
              ? (child.expectedArrivalTime ? `שעת הגעה צפויה: ${child.expectedArrivalTime.substring(0, 5)}` : '')
              : (child.arrivalTime ? `${child.gender === 2 ? 'הגיעה' : 'הגיע'} בשעה: ${formatArrivalTime(child.arrivalTime)}` : '')
            }
            {!isPending && child.arrivalTime && child.arrivalSource === 'bletag' && ' · תג'}
          </div>

          {/* כפתור פעולה */}
          <button
            onClick={onConfirm}
            disabled={loading}
            style={{
              width: '100%',
              padding: '14px',
              borderRadius: '12px',
              border: 'none',
              background: loading
                ? '#ccc'
                : isPending
                  ? 'linear-gradient(135deg, #4CAF50, #8BC34A)'
                  : 'linear-gradient(135deg, #FF9800, #FFB74D)',
              color: 'white',
              fontSize: '18px',
              fontWeight: 'bold',
              cursor: loading ? 'not-allowed' : 'pointer',
              boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
            }}
          >
            {loading ? '...' : isPending ? '✓ סמן הגעה' : '↩ החזר לטרם הגיעו'}
          </button>
        </div>
      </div>

      {/* CSS Animation */}
      <style>{`
        @keyframes modalPop {
          from {
            opacity: 0;
            transform: scale(0.9);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
      `}</style>
    </div>
  );
}
